/*
 * Partnere som viser iBønn-widgeten (dagens bønnetider) på egen nettside.
 * Backend: GET /api/v1/me/partners (partnerens admins) og GET /api/v1/admin/partners (superadmin).
 */

export interface PartnerUsageDay {
  day: string;
  views: number;
  blocked: number;
}

export interface PartnerUsageItem {
  name: string;
  views: number;
}

export interface PartnerUsage {
  today: number;
  last7Days: number;
  last30Days: number;
  blocked30Days: number;
  days: PartnerUsageDay[];
  topMosques: PartnerUsageItem[];
  origins: PartnerUsageItem[];
}

export type CreditStatus = "PENDING" | "OK" | "FAILED" | "ERROR";

export interface PartnerKey {
  id: number;
  partnerOrgId: number;
  name: string;
  publicKey: string;
  domain: string;
  pageUrl: string;
  embedUrl: string;
  embedCode: string;
  createdByEmail?: string | null;
  createdAt: string;
  revokedAt?: string | null;
  lastUsedAt?: string | null;
  usageCount: number;
  creditStatus: CreditStatus;
  creditMessage?: string | null;
  creditCheckedAt?: string | null;
  creditOkAt?: string | null;
  pausedAt?: string | null;
  creditDeadline?: string | null;
  /** Satt med «Aktiver» i superadmin: siste dag (yyyy-MM-dd) widgeten virker selv om sjekken feiler. */
  graceUntil?: string | null;
  usage: PartnerUsage;
}

export interface PartnerAdmin {
  id: number;
  email: string;
  username: string;
  grantedByEmail: string;
  createdAt: string;
}

export type PartnerStatus = "ACTIVE" | "PAUSED" | "CLOSED";

export interface PartnerOrg {
  id: number;
  name: string;
  status: PartnerStatus;
  maxKeys: number;
  createdAt: string;
  keys: PartnerKey[];
  /** Bare for superadmin. */
  orgNr?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  note?: string | null;
  createdByEmail?: string | null;
  admins?: PartnerAdmin[] | null;
}

export interface PartnerCreditCheck {
  id: number;
  checkedAt: string;
  url: string;
  httpStatus?: number | null;
  foundWidget: boolean;
  foundLink: boolean;
  result: CreditStatus;
  message?: string | null;
  triggeredBy: "NIGHTLY" | "ADMIN" | "SUPERADMIN" | "ACTIVATED";
}

export const partnerStatusLabel: Record<PartnerStatus, string> = {
  ACTIVE: "Aktiv",
  PAUSED: "Satt på pause",
  CLOSED: "Avsluttet",
};

/** Tekst og farge for status på en nøkkel (tilbaketrukket og pause går foran krediteringen). */
export const keyStatus = (
  k: PartnerKey,
): { label: string; tone: "mint" | "gold" | "rose" | "grey" } => {
  if (k.revokedAt) return { label: "Trukket tilbake", tone: "grey" };
  if (k.pausedAt) return { label: "Stoppet", tone: "rose" };
  switch (k.creditStatus) {
    case "OK":
      return { label: "Krediterer iBønn", tone: "mint" };
    case "FAILED":
      return { label: "Mangler kreditering", tone: "rose" };
    case "ERROR":
      return { label: "Fikk ikke sjekket siden", tone: "gold" };
    default:
      return { label: "Venter på sjekk", tone: "gold" };
  }
};

export const formatDateTime = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString("nb-NO", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "–";

export const formatNumber = (n: number) => n.toLocaleString("nb-NO");

/** «2026-11-17» → «17. nov. 2026» (datoen tolkes som lokal dag, ikke UTC). */
export const formatDay = (value?: string | null) => {
  if (!value) return "";
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString("nb-NO", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/** Dagens dato pluss [days] dager som yyyy-MM-dd i lokal tid. */
export const isoDayFromToday = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
